import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["centered", "children", "contextual", "onEsc", "role", "_dialogWidth", "size", "use", "width", "_closeStartRelay", "_openCompleteRelay", "dialogStyles"];
import { first, last } from '../../utils/underscore';
import { Children, Component, createRef } from 'react';
import { matches, FOCUSABLE as FOCUSABLE_SELECTOR } from '../../utils/dom';
import { Overlay } from '../Overlay';
import { Dialog } from '../Dialog/Dialog';
import { DialogCloseButton } from '../DialogCloseButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MODAL_H1_USES = ['upgrades', 'info', 'success', 'danger', 'conversational'];
export class ModalDialog extends Component {
  constructor(props) {
    super(props);
    this.handleOpenComplete = () => {
      this.setState({
        openComplete: true
      });
      this.startMutationObserver();
      this.captureFocus();
    };
    this.handleCloseStart = () => {
      if (!this._isMounted) return;
      this.stopMutationObserver();
    };
    this.startMutationObserver = () => {
      if (this._observer) return;
      const dialog = this.dialogRef.current;
      if (!dialog) return;

      // The focus should always be inside of the modal. We may need to recapture
      // it after an update because the focused element may have been unmounted.
      this._observer = new MutationObserver(this.captureFocus);
      this._observer.observe(dialog, {
        attributes: true,
        childList: true,
        subtree: true
      });

      // Disable the mutation observer during blur, as the value of
      // document.activeElement in that phase is misleading.
      window.addEventListener('blur', this.stopMutationObserver, {
        capture: true
      });
      window.addEventListener('focus', this.startMutationObserver, {
        capture: true
      });
    };
    this.stopMutationObserver = () => {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      window.removeEventListener('blur', this.stopMutationObserver, {
        capture: true
      });
      window.removeEventListener('focus', this.startMutationObserver, {
        capture: true
      });
    };
    this.captureFocus = () => {
      // If the focus is currently within the page or another dialog (or null/<body>),
      // focus on this dialog so that tabbing works. We *don't* want to capture
      // focus if the element is in a non-page/dialog element, e.g. a popover
      const dialog = this.dialogRef.current;
      if (!dialog) return;
      const activeElement = document.activeElement;
      const dialogContainsActiveElement = dialog.contains(activeElement);
      let shouldCapture = false;
      if (!activeElement || activeElement === document.body) {
        shouldCapture = true;
      } else if (!dialogContainsActiveElement) {
        // A few weird apps have multiple .page elements, also check for other dialogs...
        const containers = document.querySelectorAll('body > .page, div[role=dialog]');
        for (let i = 0; i < containers.length; i++) {
          if (containers[i].contains(activeElement)) {
            shouldCapture = true;
            break;
          }
        }
      }
      // Also, we also want to move focus to the dialog if the focus has somehow
      // wound up on a non-focusable element *within* the dialog.
      if (dialogContainsActiveElement && !matches(activeElement, FOCUSABLE_SELECTOR)) {
        shouldCapture = true;
      }
      if (shouldCapture) {
        dialog.focus();
      }
    };
    this.refCallback = ref => {
      this.dialogRef.current = ref;
    };
    this.handleKeyDown = evt => {
      const dialog = this.dialogRef.current;
      if (!dialog) return;
      if (evt.key === 'Tab') {
        // Based on https://www.w3.org/TR/wai-aria-practices/#trap_focus_div
        const activeElement = document.activeElement;
        const allFocusableElements = dialog.querySelectorAll(FOCUSABLE_SELECTOR);
        const firstFocusableElement = first(allFocusableElements);
        const lastFocusableElement = last(allFocusableElements);
        if (evt.shiftKey) {
          // Shift+Tab: If focus is on first element, cycle to last element
          if (activeElement === firstFocusableElement || activeElement === dialog) {
            evt.preventDefault();
            lastFocusableElement === null || lastFocusableElement === void 0 || lastFocusableElement.focus();
          }
        } else {
          // Unmodified Tab: If focus is on last element, cycle to first element
          if (activeElement === lastFocusableElement || activeElement === dialog) {
            evt.preventDefault();
            firstFocusableElement === null || firstFocusableElement === void 0 || firstFocusableElement.focus();
          }
        }
      } else if (evt.key === 'Escape') {
        const {
          onEsc
        } = this.props;
        if (typeof onEsc === 'function') {
          onEsc(evt);
        } else {
          const closeButton = dialog.querySelector('[data-action=close]');
          if (closeButton) closeButton.click();
        }
      }
    };
    this.headerCallback = headerOverflow => {
      if (headerOverflow !== this.state.headerOverflow) {
        this.setState({
          headerOverflow
        });
      }
    };
    this.state = {
      headerOverflow: 0,
      openComplete: !props._openCompleteRelay
    };

    // added to replace findDOMNode()
    this.dialogRef = /*#__PURE__*/createRef();
    if (props._openCompleteRelay) props._openCompleteRelay.subscribe(this.handleOpenComplete);
    if (props._closeStartRelay) props._closeStartRelay.subscribe(this.handleCloseStart);
  }
  componentDidMount() {
    this._isMounted = true;
  }
  componentWillUnmount() {
    this._isMounted = false;
    this.stopMutationObserver();
  }
  render() {
    const _this$props = this.props,
      {
        centered = false,
        children,
        contextual = false,
        role,
        _dialogWidth,
        size = 'auto',
        use = 'default',
        width = 450,
        dialogStyles
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      openComplete
    } = this.state;
    const computedUse = use;

    // If one of the children is <UIDialogCloseButton detached>, render it in
    // the overlay instead of the dialog.
    let closeButton = null;
    const dialogChildren = Children.map(children, child => {
      if (child === null) {
        return null;
        //@ts-expect-error this was migrated from UIComponents
      } else if ((child === null || child === void 0 ? void 0 : child.type) === DialogCloseButton && child.props.detached) {
        closeButton = child;
        return null;
      }
      return child;
    });
    const centerContent = ['fullscreen', 'sidebar', 'lightbox'].indexOf(computedUse) === -1;
    return /*#__PURE__*/_jsxs(Overlay, Object.assign({}, rest, {
      centerContent: centerContent,
      contextual: contextual,
      "data-component-name": "ModalDialog",
      "data-open-complete": openComplete,
      onKeyDown: this.handleKeyDown,
      ref: this.refCallback,
      width: width,
      tabIndex: -1,
      children: [/*#__PURE__*/_jsx(Dialog, Object.assign({
        _dialogWidth: _dialogWidth,
        centered: centered,
        headerOverflow: this.state.headerOverflow,
        role: role !== undefined ? role : 'dialog',
        size: size,
        use: computedUse,
        "aria-modal": true,
        headerCallback: this.headerCallback
      }, dialogStyles, {
        children: dialogChildren
      })), closeButton]
    }));
  }
}